import { useState, useRef, useEffect } from 'react';
import { AuthLayout } from '../layout/AuthLayout.jsx';
import { FormField } from '../components/FormField.jsx';
import { PasswordInput } from '../components/PasswordInput.jsx';
import { SubmitButton } from '../components/SubmitButton.jsx';
import { FormError, FormSuccess } from '../components/FormError.jsx';
import { api, ApiError } from '../api/client.js';

export function ResetPasswordPage({ navigate, token }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [success, setSuccess] = useState(null);
  const pwRef = useRef(null);

  useEffect(() => { pwRef.current?.focus(); }, []);

  if (!token) {
    return (
      <AuthLayout title="Invalid reset link">
        <FormError error="This reset link is missing a token. Request a new one." />
        <div className="auth-links">
          <a
            href="/forgot-password"
            onClick={(ev) => { ev.preventDefault(); navigate('/forgot-password'); }}
          >Request new reset link</a>
        </div>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setFieldErrors({});
    setSuccess(null);
    if (password !== confirm) {
      setFieldErrors({ new_password: 'Passwords do not match.' });
      return;
    }
    setSubmitting(true);
    try {
      await api.passwordResetConfirm({ token, new_password: password });
      setSuccess('Your password has been updated. You can now log in.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (e2) {
      if (e2 instanceof ApiError && e2.fieldErrors) {
        setFieldErrors(e2.fieldErrors);
      }
      setError(e2);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout title="Set a new password">
      <form onSubmit={handleSubmit} noValidate>
        <FormError error={error} />
        <FormSuccess message={success} />
        <FormField
          label="New password"
          htmlFor="reset-password"
          hint="At least 10 characters, with a letter and a digit."
          error={fieldErrors.new_password}
        >
          <PasswordInput
            id="reset-password"
            name="new_password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!fieldErrors.new_password}
            ref={pwRef}
            required
          />
        </FormField>
        <FormField
          label="Confirm new password"
          htmlFor="reset-password-confirm"
        >
          <PasswordInput
            id="reset-password-confirm"
            name="confirm_password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            error={!!fieldErrors.new_password}
            required
          />
        </FormField>
        <SubmitButton loading={submitting}>Update password</SubmitButton>
      </form>
      <div className="auth-links">
        <a
          href="/login"
          onClick={(ev) => { ev.preventDefault(); navigate('/login'); }}
        >Back to log in</a>
      </div>
    </AuthLayout>
  );
}
